/**
 * From the cloud's answers to what the map draws, pure: `fromActivity` for Now (a node per data model
 * that moved, the integrations with their rhythm and failures), `fromStage` for the scoped map of a
 * search (what components/data/Journey.tsx derives today: integrations from the runs, records per node,
 * the usual offsets, the links by identifier, the absent steps). No React, no DOM: unit-testable.
 */
import { prefixCode } from '../lib/systems.ts';
import dayjs from 'dayjs';
import type { Activity, ActivityDoc, Kind, RecordKind, RecordList, Run } from '../lib/api.ts';
import { count, duration, humanize } from '../lib/format.ts';
import { age, usualInterval } from '../lib/freshness.ts';
import { rhythmOf, type Rhythm } from '../lib/rhythm.ts';
import { stripSystem, systemName, systemOf } from '../lib/systems.ts';
import { dayWord } from '../stage/answer.ts';
import { MAX_FAN } from './layout.ts';
import type { MapFlow, MapLink, MapNode } from './types.ts';

/**
 * A data model as people would say it: ErpSalesOrder → sales order(s). The same function as in
 * components/data/Journey.tsx and design/primitives.tsx, copied so this module stays free of React.
 */
import { noun } from '../lib/nouns.ts';
export { noun };

const runsWord = (n: number) => `${count(n)} ${n === 1 ? 'run' : 'runs'}`;

/**
 * What an arrow says, always naming what it counts: "47 runs", "daily 18:00 · 6 runs",
 * "35 runs · 9 failed", and while a failure goes on "11 failed · 52 min".
 */
export function flowLabel(runs: number, failed: number, rhythm: Rhythm | null, ongoing?: { since: number; now: number; count?: number } | null, window?: string): string {
  if (ongoing && (ongoing.count ?? failed) > 0) return `${count(ongoing.count ?? failed)} failed · ${duration(Math.max(60_000, ongoing.now - ongoing.since))}`;
  const tail = failed > 0 ? ` · ${count(failed)} failed` : '';
  const w = window ? ` ${window}` : '';
  if (rhythm?.scheduled) {
    return `${rhythm.short} · ${runsWord(runs)}${w}${tail}`;
  }
  return `${runsWord(runs)}${w}${tail}`;
}

/** Operators and vendors a process may name as its destination: proper names, which keep their capital. */
const PROPER = new Set(['maventa', 'posti', 'apix', 'basware', 'pagero', 'peppol', 'ropo', 'fennoa', 'netvisor', 'procountor']);
/**
 * A destination as the ghost says it: common nouns in lowercase ("customers", "data warehouse"); a proper
 * name (an operator, an acronym, a word with a capital or a digit inside) as written.
 */
export function ghostNoun(external: string): string {
  return external.split(' ').map((w) => {
    if (/^[A-Z][a-z]*$/.test(w) && !PROPER.has(w.toLowerCase())) return w.toLowerCase();
    return w;
  }).join(' ');
}

/** "to e-invoice operator" at the end of a process name: where it sends when that is not on the map. */
const externalOf = (process: string) => {
  const e = /\bto\s+(.+)$/i.exec(stripSystem(process))?.[1]?.trim();
  return e ? ghostNoun(e) : null;
};

/**
 * What a node's red line counts: "failed" when some flow touching it fails the same number of runs (one
 * failure, one number), else "failed documents", so the node and its arrow are never read as disagreeing.
 */
export function failedLabelOf(cls: string, failed: number, flows: { from: string; to: string | null; failed: number }[]): string | undefined {
  if (!failed) return undefined;
  return flows.some((f) => (f.from === cls || f.to === cls) && f.failed === failed) ? 'failed' : 'failed documents';
}

// ---------------------------------------------------------------- Now

/**
 * The newest run of a process is failing: since when, as far as the newest documents tell. The fallback for
 * a cloud without `activity.failing` (which sees the whole window, not only the fifty newest documents).
 */
function ongoingFailure(docs: ActivityDoc[], process: string): number | null {
  const mine = docs.filter((d) => d.process === process).sort((a, b) => b.at - a.at);
  if (!mine.length || mine[0].status !== 'FAILED') return null;
  let since = mine[0].at;
  for (const d of mine) {
    if (d.status !== 'FAILED') break;
    since = d.at;
  }
  return since;
}

export function fromActivity(a: Activity, now: number): { nodes: MapNode[]; flows: MapFlow[] } {
  const window = a.to - (a.from ?? a.to - 86_400_000);
  const format = new Map(a.byClass.map((c) => [c.cls, c.format]));
  const total = new Map(a.byClass.map((c) => [c.cls, c.total]));
  // The count is documents today, the noun plural by it ("1 employee · 1 today", "24 customers · 24 today");
  // a data model that exists but moved nothing says so in words, never "0 today"
  const nodes: MapNode[] = a.byClass.map((c) => {
    const today = c.total > 0 ? `${count(c.total)} today` : 'none today';
    return {
      id: c.cls,
      label: noun(c.cls, c.total),
      system: c.system,
      format: c.format,
      count: c.total,
      countLabel: 'today',
      last: c.last,
      usual: c.total >= 3 ? window / c.total : null,
      failed: c.failed || undefined,
      failedWindow: 'today',
      detail: c.last && c.total > 0 ? `${today} · ${age(c.last, now)} ago` : today,
    };
  });
  // A class's documents are stored by every process whose runs touch it, so a count per run only means
  // something when a single integration touches the class
  const touches = (cls: string) => a.flows.filter((f) => f.from === cls || f.to.includes(cls)).length;
  const flows: MapFlow[] = [];
  for (const f of a.flows) {
    // The cloud's rhythm comes from a week of starts (a daily job ran once in the window); without it, the window's
    const rhythm: Rhythm = f.rhythm ?? rhythmOf(f.starts);
    // Likewise its failing streaks see the whole window; without them, as far as the newest documents tell
    const streak = a.failing ? a.failing.find((x) => x.process === f.process) ?? null : null;
    const ongoing: { since: number; now: number; count?: number } | null = a.failing
      ? streak && { since: streak.since, now, count: streak.count }
      : (() => {
        const since = ongoingFailure(a.latest, f.process);
        return since ? { since, now } : null;
      })();
    const system = f.system ?? systemOf(f.process) ?? a.byClass.find((c) => c.cls === f.from)?.system ?? '';
    const base = {
      process: f.process, system, from: f.from, runs: f.runs, failed: f.failed, rhythm,
      label: flowLabel(f.runs, f.failed, rhythm, ongoing, 'today'),
      // The streak also goes to the ghost where an out-flow ends ("none since 17:37")
      failing: ongoing ? { since: ongoing.since, count: ongoing.count } : null,
      formatIn: format.get(f.from) ?? null,
    };
    // A run of several hops (its path with what it reads): each write from the data model read last, a chain
    if (f.path && f.path.length > 2) {
      // The steps when known (a data model may be read in one step and written in another), else the path and its reads
      const reads = new Set(f.reads ?? []);
      const steps: [string, boolean][] = f.steps?.length ? f.steps : f.path.map((c) => [c, reads.has(c)]);
      let source = steps[0][0];
      const pairs: [string, string][] = [];
      for (const [c, read] of steps.slice(1)) {
        if (read) { source = c; continue; }
        if (c !== source && !pairs.some(([a, b]) => a === source && b === c)) pairs.push([source, c]);
      }
      if (pairs.length) {
        for (const [from, to] of pairs) {
          flows.push({ ...base, id: `${f.process}→${from}→${to}`, from, to, formatIn: format.get(from) ?? null, formatOut: format.get(to) ?? null });
        }
        continue;
      }
    }
    const targets = f.to.filter((t) => t !== f.from);
    if (!targets.length) {
      flows.push({ ...base, id: `${f.process}→out`, to: null, external: externalOf(f.process), formatOut: base.formatIn });
      continue;
    }
    for (const to of targets) {
      // One becomes many (or many one) when the pair has no other integration and the counts say so
      const ratioOut = (total.get(to) ?? 0) / Math.max(1, f.runs);
      const ratioIn = (total.get(f.from) ?? 0) / Math.max(1, f.runs);
      const split = touches(to) === 1 && ratioOut >= 1.5 ? Math.min(MAX_FAN, Math.round(ratioOut)) : undefined;
      // Many become one only when it is sure: the target is this integration's alone and gets about one document a run
      // (a run that carries two deliveries, each updating its own sales order, is no merge)
      const merge = !split && touches(f.from) === 1 && touches(to) === 1 && ratioIn >= 2 && ratioOut <= 1.2 ? Math.min(MAX_FAN, Math.round(ratioIn)) : undefined;
      flows.push({ ...base, id: `${f.process}→${to}`, to, formatOut: format.get(to) ?? null, split, merge });
    }
  }
  for (const n of nodes) n.failedLabel = failedLabelOf(n.id, n.failed ?? 0, flows);
  return { nodes, flows };
}

// ---------------------------------------------------------------- the stage

/** An integration: a process that moves data from one data model to another (or out to someone else). */
export interface Integration {
  process: string;
  from: string;
  to: string | null;
  /** Where it sends to when that is not among the data found ("e-invoice operator"). */
  external: string | null;
  runs: number;
  failed: number;
  rhythm: Rhythm;
  /** Documents per run, for telling a split or a merge: the median of what the runs stored. */
  perRun: number;
}

const sameSystem = (a: string, b: string) => a === b || a.startsWith(b) || b.startsWith(a);
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)] : 0;
};

/** The integrations of the runs found: per process the data models its runs touched, from its own system's to the others. */
export function integrations(runs: Run[], systemOfCls: Map<string, string>): Integration[] {
  const byProcess = new Map<string, { classes: Set<string>; runs: number; failed: number; starts: number[]; sizes: number[] }>();
  for (const r of runs) {
    if (!r.process) continue;
    const x = byProcess.get(r.process) ?? { classes: new Set<string>(), runs: 0, failed: 0, starts: [], sizes: [] };
    x.starts.push(r.first);
    x.sizes.push(r.messages);
    for (const c of r.classes) if (systemOfCls.has(c)) x.classes.add(c);
    x.runs++;
    if (r.failed > 0) x.failed++;
    byProcess.set(r.process, x);
  }
  const out: Integration[] = [];
  for (const [process, x] of byProcess) {
    const own = prefixCode(process) ?? '';
    const classes = [...x.classes].sort();
    if (classes.length === 0) continue;
    const from = classes.find((c) => sameSystem(systemOfCls.get(c)!, own)) ?? classes[0];
    const others = classes.filter((c) => c !== from);
    const external = others.length ? null : externalOf(process);
    const rhythm = rhythmOf(x.starts);
    const perRun = median(x.sizes);
    if (others.length === 0) out.push({ process, from, to: null, external, runs: x.runs, failed: x.failed, rhythm, perRun });
    for (const to of others) out.push({ process, from, to, external: null, runs: x.runs, failed: x.failed, rhythm, perRun });
  }
  return out;
}

/** Two words mean the same thing when they share their first letters: orders / order, deliveries / delivery. */
const alike = (a: string, b: string) => {
  const n = Math.min(5, a.length, b.length);
  return n >= 4 && a.slice(0, n) === b.slice(0, n);
};

/** The data model of a system that a process talks about: "Webshop Orders to ERP" in WEB → WebshopOrder. */
function kindNamed(process: string, system: string, kinds: Kind[], except?: string): string | null {
  const own = kinds.filter((k) => k.system === system && k.cls !== except);
  if (own.length <= 1) return own[0]?.cls ?? null;
  const words = humanize(stripSystem(process)).toLowerCase().split(/\W+/).filter((w) => w.length > 2);
  const score = (cls: string) => noun(cls).split(' ').filter((w) => words.some((p) => alike(p, w))).length;
  const best = [...own].sort((a, b) => score(b.cls) - score(a.cls) || b.total - a.total)[0];
  return score(best.cls) > 0 ? best.cls : null;
}

/** The system a process says it sends to ("to WMS", "to Data Warehouse"), when it is on the map. */
function systemNamed(external: string | null, systems: Iterable<string>): string | null {
  const e = external?.trim().toLowerCase();
  if (!e) return null;
  for (const s of new Set(systems)) if (s.toLowerCase() === e || systemName(s).toLowerCase() === e) return s;
  return null;
}

/**
 * The runs of a search carry only the documents the search matched, so an integration may come with
 * one end only: "[WEB] Webshop Orders to ERP" seen through ERP's sales orders alone, or "[ERP] Sales
 * Orders to WMS" without the outbound order. The process's own system and the system it names give
 * the missing end, when that data model is on the map.
 */
function resolveEnds(f: Integration, kinds: Kind[]): Integration {
  const systems = new Set(kinds.map((k) => k.system));
  const systemOfCls = new Map(kinds.map((k) => [k.cls, k.system]));
  const own = prefixCode(f.process) ?? '';
  const ownSystem = [...systems].find((s) => sameSystem(s, own));
  if (ownSystem && !sameSystem(systemOfCls.get(f.from) ?? '', own) && !f.to) {
    const src = kindNamed(f.process, ownSystem, kinds, f.from);
    if (src) return { ...f, from: src, to: f.from, external: null };
  }
  if (!f.to) {
    const target = systemNamed(f.external, systems);
    const cls = target ? kindNamed(f.process, target, kinds, f.from) : null;
    if (cls && cls !== f.from) return { ...f, to: cls, external: null };
  }
  return f;
}

/** The time a single listed record reached a data model: its own first document, or the step's cell. */
function timeOf(list: RecordList, cls: string): number | null {
  const row = list.rows[0];
  if (!row) return null;
  if (cls === list.cls) return row.first;
  const i = list.steps.findIndex((s) => s.cls === cls);
  const cell = i >= 0 ? row.steps[i] : null;
  return typeof cell === 'number' && cell > 0 ? cell : null;
}

export function fromStage(kinds: Kind[], records: RecordKind[], list: RecordList | null, runs: Run[], now: number): {
  nodes: MapNode[]; flows: MapFlow[]; links: MapLink[]; offsets: Map<string, number>;
} {
  const systemOfCls = new Map(kinds.map((k) => [k.cls, k.system]));
  const formatOf = (cls: string | null) => (cls ? kinds.find((k) => k.cls === cls)?.format ?? null : null);
  const recordsOf = (cls: string) => records.find((r) => r.cls === cls)?.records ?? kinds.find((k) => k.cls === cls)?.total ?? 0;
  const flowsRaw = integrations(runs, systemOfCls).map((f) => resolveEnds(f, kinds));

  const nodes: MapNode[] = kinds.map((k) => {
    const r = records.find((x) => x.cls === k.cls);
    const n = r?.records ?? null;
    const failed = k.status.find((s) => s.value === 'FAILED')?.count ?? 0;
    const what = n !== null
      ? n === 0 ? 'no records' : `${count(n)}${r?.more ? '+' : ''} ${n === 1 ? 'record' : 'records'}`
      : k.total === 0 ? 'no documents' : `${count(k.total)} ${k.total === 1 ? 'document' : 'documents'}`;
    return {
      id: k.cls,
      label: noun(k.cls, n ?? 2),
      system: k.system,
      format: k.format ?? null,
      count: n ?? k.total,
      countLabel: n !== null ? 'records' : 'documents',
      last: k.last,
      usual: usualInterval(k.first, k.last, k.total),
      failed: failed || undefined,
      failedLabel: failedLabelOf(k.cls, failed, flowsRaw),
      detail: k.last ? `${what} · ${age(k.last, now)} ago` : what,
    };
  });
  // A step every listed record is missing: the document that never arrived, drawn as a ghost
  if (list && list.rows.length) {
    list.steps.forEach((st, i) => {
      if (systemOfCls.has(st.cls) || !list.rows.every((r) => r.steps[i] === 'missing')) return;
      nodes.push({
        id: st.cls, label: noun(st.cls, 2), system: st.system, format: null, count: 0, countLabel: 'records',
        last: null, usual: null, absent: true, detail: 'never arrived',
      });
    });
  }

  const single = list && list.rows.length === 1 ? list : null;
  const flows: MapFlow[] = flowsRaw.map((f) => {
    const system = prefixCode(f.process) ?? systemOfCls.get(f.from) ?? '';
    let timesLabel: string | undefined;
    if (single && f.to) {
      const a = timeOf(single, f.from);
      const b = timeOf(single, f.to);
      // The hop happened when the later of the two documents came, however long after the earlier one
      if (a && b) timesLabel = `${dayWord(Math.max(a, b), now)} ${dayjs(Math.max(a, b)).format('HH:mm')} · ${duration(Math.abs(b - a))} later`;
    }
    // One becomes many when the records say so and the pair has no other integration
    let split: number | undefined;
    let merge: number | undefined;
    if (f.to && f.perRun >= 3) {
      const ratio = recordsOf(f.to) / Math.max(1, recordsOf(f.from));
      const only = (pred: (x: Integration) => boolean) => flowsRaw.filter(pred).length === 1;
      if (ratio >= 1.5 && only((x) => x.to === f.to)) split = Math.min(MAX_FAN, Math.round(ratio));
      else if (1 / ratio >= 1.5 && only((x) => x.from === f.from)) merge = Math.min(MAX_FAN, Math.round(1 / ratio));
    }
    return {
      id: `${f.process}→${f.to ?? 'out'}`, process: f.process, system, from: f.from, to: f.to, external: f.external,
      runs: f.runs, failed: f.failed, label: flowLabel(f.runs, f.failed, f.rhythm), timesLabel, rhythm: f.rhythm,
      split, merge, formatIn: formatOf(f.from), formatOut: formatOf(f.to ?? f.from),
    };
  });

  // Data models linked to the records listed without an integration bringing them: a data link
  const fed = new Set(flows.flatMap((f) => (f.to ? [f.to] : [])));
  const joined = (a: string, b: string) => flows.some((f) => (f.from === a && f.to === b) || (f.from === b && f.to === a));
  const links: MapLink[] = list
    ? list.steps.filter((st) => !fed.has(st.cls) && st.established && !joined(list.cls, st.cls)).map((st) => ({ from: list.cls, to: st.cls, via: st.via }))
    : [];

  // When each data model's records usually come, relative to the records listed
  const offsets = new Map<string, number>();
  if (list) {
    offsets.set(list.cls, 0);
    for (const st of list.steps) if (st.typicalMs !== null) offsets.set(st.cls, st.typicalMs);
  }
  return { nodes, flows, links, offsets };
}
